/* ============================================================================
 * Kids size filter — Dawn-based Shopify themes
 * ----------------------------------------------------------------------------
 * Hides adult sizes behind a "Show all sizes" button and preselects a kids
 * size — but ONLY when the customer reached the product through a collection
 * whose handle contains "kid" (headguards-for-kids ✓, headguards ✗).
 *
 * Best paired with collection-scoped links in card-product.liquid:
 *   <a href="{{ card_product.url | within: collection }}">
 *
 * Load in <head>, NOT deferred, so the CSS lands before first paint:
 *   {{ 'kids-size-filter.js' | asset_url | script_tag }}
 *
 * Not firing? Set DEBUG = true and read the console on the product page.
 * ==========================================================================*/
(() => {
  'use strict';

  /* --- config ------------------------------------------------------------ */

  const KIDS_SIZES = ['XXS', 'XS', '100', '110', '120', '130', '140', '150', '240'];

  // Substring match against the collection handle — catches kids, for-kids,
  // kids-headguards, junior-gloves, etc.
  const KIDS_TOKENS = ['kid', 'junior', 'youth', 'jnr', 'kinder'];
  const ALWAYS_KIDS = [];   // exact handles to force on
  const NEVER_KIDS  = [];   // exact handles to force off

  const USE_REFERRER = true;    // trust a click from a kids collection page
  const BREADCRUMB_TTL = 120000; // ms; used only when the referrer is stripped
  const DEBUG = false;

  // A fieldset is only filtered if its option name starts with one of these.
  // This is what stops "100" matching a Weight option, and covers the DE store.
  const SIZE_OPTION_NAMES = ['size', 'größe', 'grösse', 'groesse', 'taille', 'talla', 'maat'];

  const COPY = { prompt: 'Looking for adult sizes?', button: 'Show all sizes' };

  /* --- constants --------------------------------------------------------- */

  const UNLOCK_ATTR = 'data-show-adult';
  const MODE_ATTR   = 'data-kids-mode';
  const FLAG_ATTR   = 'data-kids-filter';
  const HIDDEN = 'sp-size-hidden';
  const MSG    = 'sp-size-message';
  const BTN_ID = 'sp-show-adult-sizes';
  const UNLOCK_KEY = 'sp:showAdultSizes'; // stores a product handle, not a flag
  const CTX_KEY    = 'sp:kidsContext';
  const CRUMB_KEY  = 'sp:lastCollection';

  const log = (...a) => { if (DEBUG) console.log('[kids-size-filter]', ...a); };

  const session = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
    del(k) { try { sessionStorage.removeItem(k); } catch (e) {} },
  };

  /* --- where are we? ----------------------------------------------------- */

  const PATH = location.pathname.toLowerCase(); // locale prefixes are fine
  const collectionInPath = (PATH.match(/\/collections\/([^/?#]+)/) || [])[1] || '';
  const productHandle    = (PATH.match(/\/products\/([^/?#]+)/) || [])[1] || '';

  // On a collection listing page: drop a breadcrumb and stop.
  if (collectionInPath && !productHandle) {
    session.set(CRUMB_KEY, collectionInPath + '|' + Date.now());
    return;
  }
  if (!productHandle) return;

  /* --- context gate ------------------------------------------------------ */

  const isKidsHandle = (h) => {
    if (!h || NEVER_KIDS.includes(h)) return false;
    if (ALWAYS_KIDS.includes(h)) return true;
    return KIDS_TOKENS.some((t) => h.includes(t));
  };

  // null = no readable same-origin referrer (missing, cross-site, or stripped)
  function referrerPath() {
    if (!USE_REFERRER || !document.referrer) return null;
    try {
      const url = new URL(document.referrer);
      if (url.origin !== location.origin) return null;
      return url.pathname.toLowerCase();
    } catch (e) {
      return null;
    }
  }

  function detect() {
    if (collectionInPath) return { handle: collectionInPath, via: 'url' };

    const ref = referrerPath();
    if (ref !== null) {
      // Only a collection *listing* counts, so context can't chain from one
      // product page to the next.
      if (!ref.includes('/products/')) {
        const h = (ref.match(/\/collections\/([^/?#]+)/) || [])[1] || '';
        if (h) return { handle: h, via: 'referrer' };
      }
      return { handle: '', via: 'referrer-not-collection' };
    }

    // Referrer stripped — fall back to the last collection seen this session.
    const crumb = session.get(CRUMB_KEY);
    if (crumb) {
      const [h, t] = crumb.split('|');
      if (Date.now() - Number(t) < BREADCRUMB_TTL) return { handle: h, via: 'breadcrumb' };
    }
    return { handle: '', via: 'none' };
  }

  const nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
  const isRestore = nav.type === 'reload' || nav.type === 'back_forward';

  const found = detect();
  const inKidsContext =
    isKidsHandle(found.handle) ||
    (isRestore && session.get(CTX_KEY) === productHandle);

  log('product:', productHandle, '| collection:', found.handle || '(none)',
      '| via:', found.via, '| nav:', nav.type, '| kids:', inKidsContext);

  if (!inKidsContext) {
    if (session.get(CTX_KEY) === productHandle) session.del(CTX_KEY);
    return;
  }
  session.set(CTX_KEY, productHandle);

  /* --- state ------------------------------------------------------------- */

  const KIDS = new Set(KIDS_SIZES.map((s) => s.trim().toUpperCase()));
  const HAS_VARIANT_PARAM = new URLSearchParams(location.search).has('variant');

  let unlocked = false;
  let autoSelectDone = false;
  let frameQueued = false;
  const ruledNames = new Set();

  /* --- styles ------------------------------------------------------------ */

  const style = document.createElement('style');
  style.id = 'sp-kids-size-filter';
  style.textContent = `
    html:not([${UNLOCK_ATTR}]) [${FLAG_ATTR}] .${HIDDEN} { display: none !important; }

    .${MSG} { margin-top: 1rem; }
    .${MSG}__text { display: block; margin-bottom: 0.6rem; font-size: 1.4rem; }
    .${MSG}__btn { width: 100%; }
    html[${UNLOCK_ATTR}] .${MSG} { display: none !important; }
  `;
  document.head.appendChild(style);

  // Dawn swaps the picker markup on every variant change, which drops our
  // classes for a frame. Keyed off the radio name (stable per section) and
  // <html>, so adult sizes stay hidden through the swap instead of flashing.
  function armFastRule(inputName) {
    if (!inputName || ruledNames.has(inputName)) return;
    ruledNames.add(inputName);

    const name = inputName.replace(/["\\]/g, '\\$&');
    const notKids = KIDS_SIZES.map((v) => `:not([value="${v}" i])`).join('');
    const sel = `html[${MODE_ATTR}]:not([${UNLOCK_ATTR}]) input[type="radio"][name="${name}"]${notKids}`;

    style.textContent += `\n${sel}, ${sel} + label { display: none !important; }`;
    document.documentElement.setAttribute(MODE_ATTR, '');
  }

  /* --- unlock (scoped to this product, not the whole session) ------------- */

  function unlock(persist) {
    if (unlocked) return;
    unlocked = true;
    document.documentElement.setAttribute(UNLOCK_ATTR, '');
    if (persist) session.set(UNLOCK_KEY, productHandle);
    document.querySelectorAll('.' + HIDDEN).forEach((el) => el.classList.remove(HIDDEN));
    const box = document.querySelector('.' + MSG);
    if (box) box.remove();
  }

  if (session.get(UNLOCK_KEY) === productHandle) unlock(false);

  document.addEventListener('click', (e) => {
    if (!e.target.closest('#' + BTN_ID)) return;
    e.preventDefault();
    unlock(true);
  });

  /* --- helpers ----------------------------------------------------------- */

  const isKids = (input) => KIDS.has((input.value || '').trim().toUpperCase());

  const isSizeName = (str) => {
    const s = (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return SIZE_OPTION_NAMES.some((n) => s.startsWith(n));
  };

  function labelFor(input) {
    if (input.id) {
      const byId = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (byId) return byId;
    }
    const next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL') return next;
    return input.closest('label');
  }

  // Dawn marks sold-out combinations with a `disabled` class on the input
  // and/or label; older versions use the disabled attribute.
  function isAvailable(input) {
    if (input.disabled || input.classList.contains('disabled')) return false;
    const label = labelFor(input);
    if (!label) return true;
    return !label.classList.contains('disabled') && label.getAttribute('aria-disabled') !== 'true';
  }

  function mark(input, hidden) {
    input.classList.toggle(HIDDEN, hidden);
    const label = labelFor(input);
    if (label) label.classList.toggle(HIDDEN, hidden);
  }

  /* --- find the size fieldset(s) ----------------------------------------- */

  function sizeGroups() {
    const groups = [];
    document.querySelectorAll('variant-selects fieldset, variant-radios fieldset').forEach((fs) => {
      const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
      if (!inputs.length || !inputs.some(isKids)) return;

      const legend = fs.querySelector('legend');   // "Size" or "Size: XS"
      const legendName = legend ? legend.textContent.split(':')[0] : '';
      if (!isSizeName(legendName) && !isSizeName(inputs[0].name)) return;

      groups.push({ fs, inputs });
    });
    if (DEBUG && !groups.length) log('no size fieldset matched on this product');
    return groups;
  }

  /* --- message box ------------------------------------------------------- */

  function ensureMessage(fs) {
    if (document.querySelector('.' + MSG)) return;

    const host = fs.closest('variant-selects, variant-radios') || fs;
    const box = document.createElement('div');
    box.className = MSG;
    box.innerHTML =
      `<span class="${MSG}__text">${COPY.prompt}</span>` +
      `<button type="button" id="${BTN_ID}" class="button button--secondary ${MSG}__btn">${COPY.button}</button>`;
    host.insertAdjacentElement('afterend', box);
  }

  /* --- main -------------------------------------------------------------- */

  function apply() {
    const groups = sizeGroups();
    if (!groups.length) return;

    // A ?variant= link is an explicit request for that size. If it points at
    // an adult one, show the full range rather than switching the customer.
    if (!unlocked && HAS_VARIANT_PARAM) {
      const checkedAdult = groups.some(({ inputs }) => {
        const checked = inputs.find((i) => i.checked);
        return checked && !isKids(checked);
      });
      if (checkedAdult) unlock(false);
    }

    groups.forEach(({ fs, inputs }) => {
      fs.setAttribute(FLAG_ATTR, '');

      if (unlocked) {
        inputs.forEach((input) => mark(input, false));
        return;
      }

      armFastRule(inputs[0].name);

      const kids = inputs.filter(isKids);
      const hasAdult = inputs.some((i) => !isKids(i));

      inputs.forEach((input) => mark(input, !isKids(input)));

      if (hasAdult) ensureMessage(fs);
      if (!autoSelectDone) autoSelect(inputs, kids);
    });
  }

  function autoSelect(inputs, kids) {
    const checked = inputs.find((i) => i.checked);
    if (!checked) return; // picker not settled yet — retry next frame
    autoSelectDone = true;

    if (isKids(checked) || HAS_VARIANT_PARAM) return;

    const target = kids.find(isAvailable) || kids[0];
    if (!target || target === checked) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* --- scheduling -------------------------------------------------------- */

  function schedule() {
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(() => { frameQueued = false; apply(); });
  }

  function boot() {
    apply();
    const target = document.querySelector('main') || document.body;
    new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('pageshow', schedule);
  }

  apply();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();